import { useRef, useState, useEffect } from "react";

/**
 * Touch-based drag-and-drop for kanban cards.
 *
 * Uses native event listeners (not React synthetic) so that
 * touchmove can be registered with { passive: false }, allowing
 * e.preventDefault() to block page scroll during drag.
 *
 * - Long-press (~220ms) to start drag — feels close to Trello.
 * - Finger must stay within 8px during the hold, otherwise it's a scroll.
 * - The card has touch-action: pan-x pan-y so a quick swipe before the
 *   long-press fires is treated as native scroll (board horizontal /
 *   column vertical). preventDefault on touchmove is only called AFTER
 *   the drag actually starts.
 * - Creates a ghost using getComputedStyle (works with CSS-in-JS).
 * - On drop, dispatches a "touch-drop" CustomEvent on the target column.
 *
 * Returns { cardRef, isDragging, didDragRef }
 *   - cardRef: attach to the card element
 *   - isDragging: boolean state for className-based visual feedback
 *   - didDragRef: ref that is true briefly after drag ends (to block onClick)
 */
const LONG_PRESS_MS = 220;
const MOVE_THRESHOLD = 8;

export default function useTouchDrag({ leadId, statusId }) {
  const cardRef = useRef(null);
  const timerRef = useRef(null);
  const ghostRef = useRef(null);
  const draggingRef = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const offsetRef = useRef({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const didDragRef = useRef(false);

  // Keep latest prop values in refs so native listeners always read current values
  const leadIdRef = useRef(leadId);
  const statusIdRef = useRef(statusId);
  useEffect(() => { leadIdRef.current = leadId; }, [leadId]);
  useEffect(() => { statusIdRef.current = statusId; }, [statusId]);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    const removeGhost = () => {
      if (ghostRef.current) {
        ghostRef.current.remove();
        ghostRef.current = null;
      }
    };

    const cleanup = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      removeGhost();
      draggingRef.current = false;
      setIsDragging(false);
      document.body.style.overflow = "";
      document.querySelectorAll("[data-column-id]").forEach((col) => {
        col.classList.remove("touch-drag-over");
      });
    };

    const startDrag = (x, y) => {
      draggingRef.current = true;
      setIsDragging(true);
      document.body.style.overflow = "hidden";

      const rect = el.getBoundingClientRect();
      const computed = window.getComputedStyle(el);
      const ghost = document.createElement("div");
      ghost.innerHTML = el.innerHTML;
      ghost.style.cssText = `
        position: fixed;
        left: ${rect.left}px;
        top: ${rect.top}px;
        width: ${rect.width}px;
        pointer-events: none;
        z-index: 10000;
        opacity: 0.9;
        transform: rotate(1.5deg) scale(1.02);
        border-radius: ${computed.borderRadius};
        background: ${computed.backgroundColor};
        border: ${computed.border};
        padding: ${computed.padding};
        font-family: ${computed.fontFamily};
        color: ${computed.color};
        box-shadow: 0 12px 28px rgba(0,0,0,0.3);
        overflow: hidden;
      `;
      document.body.appendChild(ghost);
      ghostRef.current = ghost;
      offsetRef.current = { x: x - rect.left, y: y - rect.top };

      if (navigator.vibrate) navigator.vibrate(30);
    };

    const handleTouchStart = (e) => {
      if (e.touches.length > 1) return;
      const touch = e.touches[0];
      startPos.current = { x: touch.clientX, y: touch.clientY };
      didDragRef.current = false;

      timerRef.current = setTimeout(() => {
        startDrag(touch.clientX, touch.clientY);
      }, LONG_PRESS_MS);
    };

    const handleTouchMove = (e) => {
      const touch = e.touches[0];

      if (!draggingRef.current) {
        const dx = Math.abs(touch.clientX - startPos.current.x);
        const dy = Math.abs(touch.clientY - startPos.current.y);
        if (dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) {
          if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
          }
        }
        return;
      }

      // Registered with { passive: false } — this actually prevents scroll
      e.preventDefault();

      if (ghostRef.current) {
        ghostRef.current.style.left = `${touch.clientX - offsetRef.current.x}px`;
        ghostRef.current.style.top = `${touch.clientY - offsetRef.current.y}px`;
      }

      // Hide ghost for accurate hit-test
      if (ghostRef.current) ghostRef.current.style.display = "none";
      const target = document.elementFromPoint(touch.clientX, touch.clientY);
      if (ghostRef.current) ghostRef.current.style.display = "";

      const column = target?.closest?.("[data-column-id]");
      document.querySelectorAll("[data-column-id]").forEach((col) => {
        col.classList.toggle("touch-drag-over", col === column);
      });
    };

    const handleTouchEnd = (e) => {
      if (!draggingRef.current) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        return;
      }

      // Flag to block onClick that fires after touchend
      didDragRef.current = true;
      setTimeout(() => { didDragRef.current = false; }, 300);

      const touch = e.changedTouches[0];
      removeGhost();

      const target = document.elementFromPoint(touch.clientX, touch.clientY);
      const column = target?.closest?.("[data-column-id]");

      if (column) {
        const cardList = column.querySelector("[data-card-list]");
        let targetIndex = 0;
        if (cardList) {
          const cards = Array.from(cardList.querySelectorAll("[data-card-id]"));
          targetIndex = cards.length;
          for (let i = 0; i < cards.length; i++) {
            const rect = cards[i].getBoundingClientRect();
            if (touch.clientY < rect.top + rect.height / 2) {
              targetIndex = i;
              break;
            }
          }
        }

        column.dispatchEvent(
          new CustomEvent("touch-drop", {
            bubbles: false,
            detail: {
              leadId: Number(leadIdRef.current),
              fromStatusId: Number(statusIdRef.current),
              toStatusId: Number(column.dataset.columnId),
              targetIndex,
            },
          })
        );
      }

      cleanup();
    };

    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchmove", handleTouchMove, { passive: false });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchmove", handleTouchMove);
      el.removeEventListener("touchend", handleTouchEnd);
      if (timerRef.current) clearTimeout(timerRef.current);
      removeGhost();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { cardRef, isDragging, didDragRef };
}
